import { SECURITY_EVENT_TYPES } from '../constants';
import { isWEB } from '../../helpers/getPlatformType';

/**
 * Network Security Detector
 * 
 * Monitors network connections, SSL/TLS security, certificate validation,
 * and detects network-based attacks and anomalies.
 */
export class NetworkSecurityDetector {
    constructor(onSecurityEvent) {
        this.onSecurityEvent = onSecurityEvent;
        this.initialized = false;
        this.connectionHistory = [];
        this.trustedDomains = new Set();
        this.suspiciousConnections = [];
        this.certificateCache = new Map();
        this.networkBaseline = null;
        this.lastNetworkCheck = Date.now();
    }

    /**
     * Initialize the network security detector
     */
    async initialize() {
        try {
            // Initialize trusted domains
            this.initializeTrustedDomains();
            
            // Set up network monitoring
            this.setupNetworkMonitoring();
            
            // Create network baseline
            await this.createNetworkBaseline();
            
            // Set up connection interceptors
            this.setupConnectionInterceptors();
            
            this.initialized = true;
        } catch (error) {
            console.error('RASP - Failed to initialize Network Security Detector:', error);
            throw error;
        }
    }

    /**
     * Initialize trusted domains list
     */
    initializeTrustedDomains() {
        // Add known trusted domains for the application
        const trustedDomains = [
            'yarooms.com',
            'api.yarooms.com',
            'api.eu.yarooms.com',
            'api.ca.yarooms.com',
            'expo.dev',
            'u.expo.dev'
        ];

        trustedDomains.forEach(domain => this.trustedDomains.add(domain));
    }

    /**
     * Set up network monitoring
     */
    setupNetworkMonitoring() {
        // Monitor fetch requests if available
        this.monitorFetchRequests();
        
        // Monitor XMLHttpRequest if available
        this.monitorXMLHttpRequests();
        
        // Monitor WebSocket connections
        this.monitorWebSocketConnections();
    }

    /**
     * Create network baseline
     */
    async createNetworkBaseline() {
        try {
            this.networkBaseline = {
                timestamp: Date.now(),
                connectionCount: 0,
                trustedConnections: 0,
                averageResponseTime: 0,
                protocolDistribution: {},
                domainDistribution: {}
            };
            
            console.log('RASP - Network baseline created');
            
        } catch (error) {
            console.warn('RASP - Failed to create network baseline:', error);
        }
    }

    /**
     * Set up connection interceptors
     */
    setupConnectionInterceptors() {
        try {
            // Intercept and monitor network connections
            this.interceptFetch();
            this.interceptXMLHttpRequest();
            
        } catch (error) {
            console.warn('RASP - Failed to setup connection interceptors:', error);
        }
    }

    /**
     * Monitor fetch requests
     */
    monitorFetchRequests() {
        if (typeof fetch === 'undefined') return;

        try {
            // Store original fetch
            const originalFetch = fetch;
            const self = this;

            // Override fetch to monitor requests
            global.fetch = async function(url, options = {}) {
                const startTime = Date.now();

                try {
                    // Only analyze HTTP/HTTPS URLs to avoid Pusher.js internal requests
                    if (url && typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'))) {
                        // Analyze request before sending
                        self.analyzeOutgoingRequest(url, options, 'fetch');
                    }

                    // Make the actual request
                    const response = await originalFetch(url, options);

                    // Only analyze HTTP/HTTPS responses
                    if (url && typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'))) {
                        // Analyze response
                        const endTime = Date.now();
                        self.analyzeResponse(url, response, endTime - startTime, 'fetch');
                    }

                    return response;
                } catch (error) {
                    // Only analyze HTTP/HTTPS failed requests
                    if (url && typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'))) {
                        // Analyze failed request
                        const endTime = Date.now();
                        self.analyzeFailedRequest(url, error, endTime - startTime, 'fetch');
                    }
                    throw error;
                }
            };

        } catch (error) {
            console.warn('RASP - Failed to monitor fetch requests:', error);
        }
    }

    /**
     * Monitor XMLHttpRequest
     */
    monitorXMLHttpRequests() {
        if (typeof XMLHttpRequest === 'undefined') return;

        try {
            const originalXHR = XMLHttpRequest;
            const self = this;

            global.XMLHttpRequest = function() {
                const xhr = new originalXHR();
                const originalOpen = xhr.open;
                const originalSend = xhr.send;
                
                let requestUrl = '';
                let requestMethod = '';
                let startTime = 0;

                xhr.open = function(method, url, ...args) {
                    requestUrl = url;
                    requestMethod = method;
                    return originalOpen.apply(this, [method, url, ...args]);
                };

                xhr.send = function(data) {
                    startTime = Date.now();
                    
                    // Analyze outgoing request
                    self.analyzeOutgoingRequest(requestUrl, { method: requestMethod, body: data }, 'xhr');
                    
                    return originalSend.apply(this, arguments);
                };

                // Monitor response
                xhr.addEventListener('loadend', function() {
                    const endTime = Date.now();
                    const responseTime = endTime - startTime;
                    
                    if (xhr.status >= 200 && xhr.status < 400) {
                        self.analyzeResponse(requestUrl, { status: xhr.status, headers: {} }, responseTime, 'xhr');
                    } else {
                        self.analyzeFailedRequest(requestUrl, new Error(`HTTP ${xhr.status}`), responseTime, 'xhr');
                    }
                });

                return xhr;
            };

        } catch (error) {
            console.warn('RASP - Failed to monitor XMLHttpRequest:', error);
        }
    }

    /**
     * Monitor WebSocket connections
     */
    monitorWebSocketConnections() {
        if (typeof WebSocket === 'undefined') return;

        try {
            const originalWebSocket = WebSocket;
            const self = this;

            global.WebSocket = function(url, protocols) {
                // Analyze WebSocket connection
                self.analyzeWebSocketConnection(url, protocols);
                
                return new originalWebSocket(url, protocols);
            };

        } catch (error) {
            console.warn('RASP - Failed to monitor WebSocket connections:', error);
        }
    }

    /**
     * Intercept fetch for detailed monitoring
     */
    interceptFetch() {
        // This is handled in monitorFetchRequests
    }

    /**
     * Intercept XMLHttpRequest for detailed monitoring
     */
    interceptXMLHttpRequest() {
        // This is handled in monitorXMLHttpRequests
    }

    /**
     * Perform network security check
     */
    async performCheck() {
        if (!this.initialized) return;

        try {
            // Analyze recent connections
            await this.analyzeRecentConnections();
            
            // Check for suspicious patterns
            await this.checkSuspiciousPatterns();
            
            // Validate certificates
            await this.validateCertificates();
            
            // Update network baseline
            await this.updateNetworkBaseline();
            
            // Cleanup old data
            await this.cleanupOldData();
            
        } catch (error) {
            console.warn('RASP - Network security check failed:', error);
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.NETWORK_ANOMALY, {
                message: 'Network security check failed',
                details: { error: error.message }
            });
        }
    }

    /**
     * Analyze outgoing request
     */
    analyzeOutgoingRequest(url, options, type) {
        try {
            // Skip analysis for invalid URLs or non-HTTP protocols
            if (!url || typeof url !== 'string') {
                return; // Skip invalid URLs
            }

            // Skip relative URLs and non-HTTP protocols
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
                return; // Skip non-HTTP URLs
            }

            const urlObj = new URL(url);
            const domain = urlObj.hostname;
            const protocol = urlObj.protocol;
            
            // Check if domain is trusted
            const isTrusted = this.isDomainTrusted(domain);
            
            // Check for insecure protocols
            if (protocol === 'http:' && !this.isLocalhost(domain)) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.INSECURE_CONNECTION, {
                    message: 'Insecure HTTP connection detected',
                    details: { url, domain, protocol, type }
                });
            }
            
            // Check for suspicious domains
            if (!isTrusted && this.isSuspiciousDomain(domain)) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.NETWORK_ANOMALY, {
                    message: 'Connection to suspicious domain',
                    details: { url, domain, type }
                });
            }
            
            // Log connection
            this.logConnection({
                url,
                domain,
                protocol,
                method: options.method || 'GET',
                type,
                trusted: isTrusted,
                timestamp: Date.now(),
                direction: 'outgoing'
            });
            
        } catch (error) {
            // Silently skip invalid URLs instead of logging errors
            // This prevents spam from Pusher.js internal requests
            return;
        }
    }

    /**
     * Analyze response
     */
    analyzeResponse(url, response, responseTime, type) {
        try {
            // Skip analysis for invalid URLs
            if (!url || typeof url !== 'string' || (!url.startsWith('http://') && !url.startsWith('https://'))) {
                return; // Skip invalid URLs
            }

            const urlObj = new URL(url);
            
            // Check for unusually slow responses
            if (responseTime > 10000) { // 10 seconds
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.NETWORK_ANOMALY, {
                    message: 'Unusually slow network response',
                    details: { url, responseTime, type }
                });
            }
            
            // Check response status
            if (response.status >= 400) {
                this.analyzeErrorResponse(url, response, type);
            }
            
            // Update connection log
            this.updateConnectionLog(url, {
                status: response.status,
                responseTime,
                success: true
            });
            
        } catch (error) {
            // Silently skip invalid URLs instead of logging errors
            return;
        }
    }

    /**
     * Analyze failed request
     */
    analyzeFailedRequest(url, error, responseTime, type) {
        try {
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.NETWORK_ANOMALY, {
                message: 'Network request failed',
                details: { 
                    url, 
                    error: error.message, 
                    responseTime, 
                    type 
                }
            });
            
            // Update connection log
            this.updateConnectionLog(url, {
                error: error.message,
                responseTime,
                success: false
            });
            
        } catch (error) {
            console.warn('RASP - Failed to analyze failed request:', error);
        }
    }

    /**
     * Analyze WebSocket connection
     */
    analyzeWebSocketConnection(url, protocols) {
        try {
            // Skip analysis for invalid URLs
            if (!url || typeof url !== 'string') {
                return; // Skip invalid URLs
            }

            // Skip non-WebSocket URLs
            if (!url.startsWith('ws://') && !url.startsWith('wss://')) {
                return; // Skip non-WebSocket URLs
            }

            const urlObj = new URL(url);
            const domain = urlObj.hostname;
            const protocol = urlObj.protocol;
            
            // Check for insecure WebSocket connections
            if (protocol === 'ws:' && !this.isLocalhost(domain)) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.INSECURE_CONNECTION, {
                    message: 'Insecure WebSocket connection detected',
                    details: { url, domain, protocol, protocols }
                });
            }
            
            // Log WebSocket connection
            this.logConnection({
                url,
                domain,
                protocol,
                type: 'websocket',
                protocols,
                trusted: this.isDomainTrusted(domain),
                timestamp: Date.now(),
                direction: 'outgoing'
            });
            
        } catch (error) {
            // Silently skip invalid WebSocket URLs
            return;
        }
    }

    /**
     * Monitor socket event (for Socket.IO/Soketi)
     */
    monitorSocketEvent(eventData) {
        try {
            // Monitor socket events for security issues
            if (eventData.type === 'error') {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.NETWORK_ANOMALY, {
                    message: 'Socket connection error',
                    details: eventData
                });
            }
            
            if (eventData.type === 'disconnect' && eventData.reason !== 'client disconnect') {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.NETWORK_ANOMALY, {
                    message: 'Unexpected socket disconnection',
                    details: eventData
                });
            }
            
        } catch (error) {
            console.warn('RASP - Failed to monitor socket event:', error);
        }
    }

    /**
     * Check if domain is trusted
     */
    isDomainTrusted(domain) {
        return this.trustedDomains.has(domain) || this.isLocalhost(domain);
    }

    /**
     * Check if domain is localhost
     */
    isLocalhost(domain) {
        return domain === 'localhost' || 
               domain === '127.0.0.1' || 
               domain.startsWith('192.168.') ||
               domain.startsWith('10.') ||
               domain.startsWith('172.');
    }

    /**
     * Check if domain is suspicious
     */
    isSuspiciousDomain(domain) {
        const suspiciousPatterns = [
            /\d+\.\d+\.\d+\.\d+/, // IP addresses (not localhost)
            /[a-z0-9]{20,}\./, // Very long random subdomains
            /bit\.ly|tinyurl|t\.co/, // URL shorteners
            /\.tk$|\.ml$|\.ga$|\.cf$/, // Suspicious TLDs
        ];
        
        return suspiciousPatterns.some(pattern => pattern.test(domain));
    }

    /**
     * Log connection
     */
    logConnection(connectionInfo) {
        this.connectionHistory.push(connectionInfo);
        
        // Keep only recent connections (last 500)
        if (this.connectionHistory.length > 500) {
            this.connectionHistory = this.connectionHistory.slice(-500);
        }
    }

    /**
     * Update connection log with response info
     */
    updateConnectionLog(url, responseInfo) {
        const connection = this.connectionHistory
            .slice()
            .reverse()
            .find(conn => conn.url === url);
            
        if (connection) {
            Object.assign(connection, responseInfo);
        }
    }

    /**
     * Analyze recent connections
     */
    async analyzeRecentConnections() {
        const recentConnections = this.connectionHistory.slice(-50);
        
        // Check for too many failed connections
        const failedConnections = recentConnections.filter(conn => !conn.success);
        if (failedConnections.length > 10) {
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.NETWORK_ANOMALY, {
                message: 'High number of failed network connections',
                details: {
                    failedCount: failedConnections.length,
                    totalCount: recentConnections.length
                }
            });
        }
        
        // Check for connections to many different domains (LESS AGGRESSIVE)
        const uniqueDomains = new Set(recentConnections.map(conn => conn.domain));
        if (uniqueDomains.size > 100) { // Increased from 20 to 100
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.NETWORK_ANOMALY, {
                message: 'Connections to extremely many domains',
                details: {
                    domainCount: uniqueDomains.size,
                    domains: Array.from(uniqueDomains).slice(0, 10)
                }
            });
        }
    }

    /**
     * Check for suspicious patterns
     */
    async checkSuspiciousPatterns() {
        // Check for rapid-fire connections (MUCH LESS AGGRESSIVE)
        const now = Date.now();
        const recentConnections = this.connectionHistory.filter(
            conn => now - conn.timestamp < 10000 // Last 10 seconds
        );

        if (recentConnections.length > 500) { // Increased from 50 to 500
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.NETWORK_ANOMALY, {
                message: 'Extremely rapid network connections detected',
                details: {
                    connectionCount: recentConnections.length,
                    timeWindow: '10 seconds'
                }
            });
        }
    }

    /**
     * Validate certificates (limited in React Native)
     */
    async validateCertificates() {
        // Certificate validation is limited in React Native
        // This is more of a placeholder for future enhancements
        try {
            // Could implement basic certificate checks if needed
        } catch (error) {
            console.warn('RASP - Certificate validation failed:', error);
        }
    }

    /**
     * Update network baseline
     */
    async updateNetworkBaseline() {
        if (!this.networkBaseline) return;

        try {
            const recentConnections = this.connectionHistory.slice(-100);
            
            this.networkBaseline.connectionCount = this.connectionHistory.length;
            this.networkBaseline.trustedConnections = recentConnections.filter(conn => conn.trusted).length;
            
            // Update protocol distribution
            const protocols = {};
            recentConnections.forEach(conn => {
                protocols[conn.protocol] = (protocols[conn.protocol] || 0) + 1;
            });
            this.networkBaseline.protocolDistribution = protocols;
            
            // Update domain distribution
            const domains = {};
            recentConnections.forEach(conn => {
                domains[conn.domain] = (domains[conn.domain] || 0) + 1;
            });
            this.networkBaseline.domainDistribution = domains;
            
        } catch (error) {
            console.warn('RASP - Failed to update network baseline:', error);
        }
    }

    /**
     * Analyze error response
     */
    analyzeErrorResponse(url, response, type) {
        if (response.status === 401 || response.status === 403) {
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.UNAUTHORIZED_ACCESS, {
                message: 'Unauthorized network access attempt',
                details: { url, status: response.status, type }
            });
        }
    }

    /**
     * Clean up old data
     */
    async cleanupOldData() {
        const now = Date.now();
        const oneHourAgo = now - 3600000;
        
        // Remove old connections
        this.connectionHistory = this.connectionHistory.filter(
            conn => conn.timestamp > oneHourAgo
        );
        
        // Remove old suspicious connections
        this.suspiciousConnections = this.suspiciousConnections.filter(
            conn => conn.timestamp > oneHourAgo
        );
    }

    /**
     * Report a security event
     */
    reportSecurityEvent(eventType, eventData) {
        if (this.onSecurityEvent) {
            this.onSecurityEvent(eventType, eventData);
        }
    }

    /**
     * Get network statistics
     */
    getStatistics() {
        return {
            baseline: this.networkBaseline,
            connectionCount: this.connectionHistory.length,
            trustedDomains: Array.from(this.trustedDomains),
            suspiciousConnections: this.suspiciousConnections.length,
            lastCheck: this.lastNetworkCheck
        };
    }

    /**
     * Cleanup the detector
     */
    cleanup() {
        try {
            this.connectionHistory = [];
            this.suspiciousConnections = [];
            this.certificateCache.clear();
            this.initialized = false;
            
            console.log('RASP - Network Security Detector cleanup completed');
        } catch (error) {
            console.error('RASP - Network Security Detector cleanup failed:', error);
        }
    }
}
